import React, { useState, useEffect } from 'react';
import { Brain, Waves, Heart, ArrowDown, ArrowRightCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import WhatsAppCommunityBanner from '../components/utilities/WhatsAppCommunityBanner';

const TraumaPage: React.FC = () => {
    const [currentText, setCurrentText] = useState("Healing");
    const textCycle = ["Healing", "Recovering", "Empowering", "Transforming"];

    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            const currentIndex = textCycle.indexOf(currentText);
            const nextIndex = (currentIndex + 1) % textCycle.length;
            setCurrentText(textCycle[nextIndex]);
        }, 2000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [currentText]);
    const traumaCards = [
        {
            icon: <Brain size={48} color="#8E44AD" />,
            title: "Understanding Trauma",
            description: "Trauma is not just a memory, but a complex experience stored in both mind and body. Our approach recognizes the profound impact of traumatic experiences.",
            image: "/images/truama1.png"
        },
        {
            icon: <Waves size={48} color="#E2B010" />,
            title: "Holistic Healing",
            description: "We provide a compassionate, body-informed approach that helps you navigate and integrate traumatic experiences at your own pace.",
            image: "/images/trauma2.png"
        },
        {
            icon: <Heart size={48} color="#A1ADA5" />,
            title: "Personalized Support",
            description: "Each trauma journey is unique. We offer tailored therapeutic interventions that respect your individual experience and healing path.",
            image: "/images/trauma3.png"
        }
    ];

    const traumaTestimonials = [
        {
            quote: "Working through my trauma has been challenging, but this program helped me rediscover my inner strength. The personalized approach made me feel seen and supported throughout my healing journey.",
            avatar: "/images/avatar1.png"
        },
        {
            quote: "I was skeptical about therapy at first, but the holistic approach here truly transformed my understanding of my experiences. I've learned to embrace my resilience and find peace.",
            avatar: "/images/avatar4.png"
        },
        {
            quote: "After years of struggling silently, I finally found a safe space to process my trauma. The compassionate therapists helped me reconnect with myself and rebuild my life.",
            avatar: "/images/avatar5.png"
        }
    ];

    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>Psychologist for Trauma Therapy in India| Affordable Online Counselling</title>
                <meta name="description" content="Specialized therapy for complex trauma, childhood trauma, grief in India. Heal with experienced,
culturally sensitive therapists via online or in-person sessions." />
                {/* Open Graph Tags */}
                <meta property="og:title" content="Psychologist for Trauma Therapy in India| Affordable Online Counselling" />
                <meta property="og:description" content="Specialized therapy for complex trauma, childhood trauma, grief in India. Heal with experienced,
culturally sensitive therapists via online or in-person sessions." />
                <meta property="og:type" content="healthcare.service" />
                <meta property="og:image" content="/images/trauma-1.png" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Psychologist for Trauma Therapy in India| Affordable Online Counselling" />
                <meta name="twitter:description" content="Navigate your healing journey with expert, compassionate trauma counseling. Safe space to process experiences and rebuild emotional wellness." />

                {/* Keywords */}
                <meta name="keywords" content="trauma counseling, emotional healing, mental health support, recovery from painful experiences, stress management, emotional resilience, healing trauma, psychological support, personal growth, trauma therapy, coping with past hurt" />

                {/* Schema.org JSON-LD */}
                <script type="application/ld+json">
                    {`
          {
            "@context": "https://schema.org",
            "@type": "MedicalService",
            "name": "Trauma Support Services",
            "description": "Comprehensive trauma counseling and emotional healing support",
            "provider": {
              "@type": "MedicalOrganization",
              "name": "Greh Pravesh Therapy",
              "description": "Professional mental health services specializing in trauma recovery"
            },
            "serviceType": [
              {
                "@type": "Service",
                "name": "Individual Trauma Counseling",
                "description": "One-on-one support for processing and healing from traumatic experiences"
              },
              {
                "@type": "Service",
                "name": "Holistic Trauma Recovery",
                "description": "Comprehensive approach addressing emotional and physical aspects of trauma"
              }
            ],
            "offers": {
              "@type": "Offer",
              "price": "Varies",
              "priceCurrency": "INR",
              "availability": "https://schema.org/InStock"
            },
            "areaServed": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": "0",
                "longitude": "0"
              },
              "geoRadius": "100 km"
            }
          }
        `}
                </script>
            </Helmet>
            <div className="bg-white">
                {/* Landing Section */}
                <section className="curvy-bg-container relative min-h-screen flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-golden-yellow to-golden-yellow">
                    {/* Background layers */}
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-purple to-purple rounded-br-[100%] z-0"></div>
                    <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0" style={{ backgroundImage: "url('/images/trauma-1.png')" }}></div>

                    <div className="w-full md:w-7/12 z-10 flex items-center text-center mt-28 md:mt-0 text-center px-6 md:pl-24 md:pr-0 relative">
                        <div className="max-w-2xl w-full space-y-6 md:mt-20">
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-4xl md:text-4xl text-white font-semibold md:font-bold mt-32 md:mt-0"
                            >
                                Therapy for Complex Trauma and Grief
                            </motion.h1>

                            <motion.div
                                className="pt-8 space-y-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.7 }}
                            >
                                <button
                                    onClick={() => navigate("/book-appointment")}
                                    className="bg-white text-purple flex items-center justify-center text-center px-6 py-3 text-sm md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all w-full md:w-auto mx-auto">
                                    Begin Your Healing Journey
                                    <ArrowRightCircle className="ml-3 w-7 h-7 text-purple-700" />
                                </button>

                                {/* Mobile Image Section */}
                                <motion.div
                                    className="block md:hidden w-full h-96 md:h-full"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.9, delay: 3 }}
                                >
                                    <img
                                        src="/images/trauma-1.png"
                                        alt="trauma support"
                                        className="w-full h-full object-cover rounded-xl opacity-80"
                                    />
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>

                    {/* Desktop Image Section */}
                    <motion.div
                        className="hidden md:block absolute top-0 right-0 w-5/12 h-full z-0"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 3 }}
                    >
                        <img
                            src="/images/trauma-1.png"
                            alt="trauma support"
                            className="absolute inset-0 w-full h-full object-cover opacity-70"
                        />
                    </motion.div>
                </section>

                {/* Scroll Indicator */}
                <div className="text-center py-4 bg-white">
                    <ArrowDown
                        className="animate-bounce mx-auto text-purple"
                        size={32}
                    />
                </div>

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 space-y-12 sm:space-y-16 lg:space-y-20">
                    <section className="grid md:grid-cols-3 gap-8">
                        {traumaCards.map((card, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-xl"
                            >
                                <img
                                    src={card.image}
                                    alt={card.title}
                                    className="w-full h-48 object-cover"
                                />
                                <div className="p-6">
                                    <div className="flex justify-center mb-4">
                                        {card.icon}
                                    </div>
                                    <h2 className="text-2xl font-raleway-regular text-charcol text-center mb-4">
                                        {card.title}
                                    </h2>
                                    <p className="text-center font-lato-regular text-sage">
                                        {card.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </section>

                    <section className="max-w-7xl mx-auto">
                        <div className="max-w-5xl mx-auto">
                            <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg border border-emerald-200">
                                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                                        Our mind and feelings are like a house: a safe space to just be. When difficult and scary incidents
                                        happen in our lives, it's like a storm hitting the house. Things change and make us feel as if the safe
                                        space has been taken away. In those times, seeking help from a specialized psychologist for complex
                                        trauma and grief may clear the way back home.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        One might go through a single big storm, like a natural disaster or a bad accident. This can cause a
                                        lot of damage, which is described as Post-Traumatic Stress Disorder (PTSD).
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                                        But what if the storms aren't just one big one, but persistent, maybe for a long time, like someone is
                                        constantly throwing rocks at it? That's more like Complex Trauma.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* What's Complex Trauma Section */}
                    <section className="max-w-7xl mx-auto">
                        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 xl:p-16 shadow-lg border border-blue-200">
                            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                                    What's Complex Trauma?
                                </h2>
                            </div>

                            <div className="space-y-6 sm:space-y-8 lg:space-y-10 max-w-5xl mx-auto">
                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                    Complex Post-Traumatic Stress Disorder (CPTSD) is a term used for the challenges that come from
                                    prolonged exposure to trauma, meaning it happened for a long time, not just once.
                                </p>
                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                    Because it happens over a long period, it affects a lot more things than just one event might. CPTSD
                                    involves a complex range of symptoms that go beyond what you might see in typical PTSD.
                                </p>

                                {/* Complex Trauma Effects Box */}
                                <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg border border-gray-200">
                                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-gray-800 mb-6 sm:mb-8">
                                        Complex Trauma may affect:
                                    </h3>
                                    <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg md:text-xl text-gray-700">
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Your identity</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>How you see yourself or who you feel you are</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Your self-esteem, how much you value yourself</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Your relationships with others</span>
                                        </li>
                                    </ul>
                                </div>

                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                                    These repeated storms can shake the very foundation of your house, instilling fears that it may not
                                    stay standing and make it difficult to feel safe inside or invite others over. That's how complex
                                    trauma affects a person's inner world and connections with others.
                                </p>
                            </div>
                        </div>
                    </section>


                    <section className="max-w-7xl mx-auto">
                        <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 xl:p-16 shadow-lg border border-violet-200">
                            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                                    What's Trauma-Informed Therapy?
                                </h2>
                            </div>

                            <div className="space-y-6 sm:space-y-8 lg:space-y-10 max-w-5xl mx-auto">
                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                    CPTSD-informed therapy is seen as really important, or "vital," because it's designed to help people
                                    deal with these multifaceted impacts of this kind of trauma. It focuses on the enduring effects of
                                    complex trauma to help people recover more effectively.
                                </p>

                                {/* Therapy Goals Box */}
                                <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg border border-gray-200">
                                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-gray-800 mb-6 sm:mb-8">
                                        This type of therapy aims to:
                                    </h3>
                                    <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg md:text-xl text-gray-700">
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Create safe space for people to explore their experiences and heal</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Help them build resilience (which is like the ability of your house to withstand future storms)</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Ensure overall well-being</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="space-y-4 sm:space-y-6">
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        Many specific situations can lead to complex trauma, especially in a place like India. Let's understand
                                        this with an example from the lens of family dynamics in Indian urban families and how they
                                        contribute to complex trauma and related issues.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        Indian culture often promotes a hierarchical collectivistic society, meaning there's a strong emphasis
                                        on group identity and clear structures or ranks within families and society. In this culture, a person's
                                        sense of identity is often based on their relationship with others. Parenting styles can lean towards
                                        authoritarian methods for discipline.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        The multiple factors within these family dynamics like attachment patterns (how you form bonds
                                        with others), authoritarian parenting, a lack of individuation (not being encouraged to develop your
                                        own separate identity), intergenerational transmission of trauma (when trauma effects are passed
                                        down through families), marital discord (parents having a difficult relationship), and the structure of
                                        nuclear and extended families (who lives together and how they interact), can all play a role in
                                        developing complex trauma.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                                        It's like your safe space being threatened within the family structure itself, and these specific family
                                        patterns and historical difficulties make those storms even harder to handle.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        Addressing family dynamics and working on repairing attachment bonds are mentioned as important
                                        goals in therapy for complex trauma in this context.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        Our therapists in India have experience working with traumatic stress and trauma healing. Cultural
                                        competency in healthcare, in general, is about professionals being able to understand and respect
                                        the varied needs, beliefs, and values of the people they serve, especially traumatized individuals,
                                        because cultural factors play a big role in how people experience, cope with, and recover from
                                        trauma.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>


                    {/* What's Grief Section */}
                    <section className="max-w-7xl mx-auto">
                        <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 xl:p-16 shadow-lg border border-amber-200">
                            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                                    What's Grief?
                                </h2>
                            </div>

                            <div className="space-y-6 sm:space-y-8 lg:space-y-10 max-w-5xl mx-auto">
                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                    Grief is different from trauma, although sometimes they can overlap. Grief is what happens when
                                    you lose someone you care about. People often describe it as one of the most profound and heart-
                                    wrenching experiences anyone can go through.
                                </p>
                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                    It's not just feeling sad; grief is a complex and multi-dimensional emotion. This means it has many
                                    different layers and parts to it. When you're grieving, you might feel a wide range of feelings, like
                                    sadness (of course!), but also anger, guilt, anxiety, and even relief.
                                </p>

                                {/* Grief Symptoms Box */}
                                <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg border border-gray-200">
                                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-gray-800 mb-6 sm:mb-8">
                                        Some people feel like they are in a fog. Grief can also affect your body, causing physical symptoms like:
                                    </h3>
                                    <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg md:text-xl text-gray-700">
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Trouble sleeping (sleep disturbances)</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Not wanting to eat (loss of appetite)</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Feeling pain in your body (chronic pain)</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                                            <span>Stress and anxiety are also common when you're grieving</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Culturally Sensitive Approach Section */}
                    <section className="max-w-7xl mx-auto">
                        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight px-4">
                                Culturally Sensitive Approach
                            </h2>
                        </div>

                        <div className="max-w-5xl mx-auto">
                            <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg border border-slate-200">
                                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                                        Grief is a journey, not a destination, and there's no set timeline for how long it takes to heal. It's
                                        something you work through step by step, not something you just get over.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        For example, parents who lose a child or had a stillbirth suffer from disbelief, severe pain, and
                                        helplessness. Mothers often express their grief openly, sometimes even fainting, while fathers also
                                        felt severe grief but don't always show it openly.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        Some parents feel guilt or blame themselves or others for the loss. The loss could also affect their
                                        relationship as a couple, sometimes causing difficulties. Many parents could also experience trouble
                                        sleeping, eating, and other psychological disturbances.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                                        In another example of how situational and specific grief can be, consider this:
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        During the pandemic, restrictions made it hard to follow the traditional Hindu death rituals, which
                                        are usually a way for families and communities to mourn together. This forced people to grieve
                                        alone instead of grieving together.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                        Not being able to participate in ceremonies like "Shraddh" (a final feast in remembrance), meant
                                        that mourners lost the chance to grieve as a group, which normally helps them feel better and
                                        accept the loss.
                                    </p>
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                                        When the pandemic stopped these rituals, it made the grief harder because people couldn't get the
                                        sense of closure or healing that the physical acts of mourning and collective support usually provide.
                                        This shows how important cultural practices are in how people experience and cope with grief.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>




                    {/* Talking Beyond Stigma Section */}
                    <section className="max-w-7xl mx-auto">
                        <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 xl:p-16 shadow-lg border border-rose-200">
                            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight px-4">
                                    Talking Beyond Stigma
                                </h2>
                            </div>

                            <div className="max-w-5xl mx-auto">
                                <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg border border-gray-200">
                                    <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                            Some experts might use words like depression or post-traumatic stress to describe these feelings,
                                            the people often used their own words, like "chinta" (worried). This is important because using
                                            words like mentally ill can cause even more unfair treatment and shame in India.
                                        </p>
                                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                                            Therapy can offer a safe place to talk about feelings and learn ways to cope. There are different
                                            types of therapy that can help. Therapists can use approaches like CBT, DBT, Motivational
                                            Interviewing, SFBT, ACT, and EMDR to help people process their emotions and develop healthier
                                            ways to handle difficult feelings.
                                        </p>
                                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-bold bg-gradient-to-r from-rose-100 to-pink-100 p-4 sm:p-6 rounded-xl border border-rose-200">
                                            Both complex trauma and grief can be very hard, affect your mind and body, and are often shaped
                                            by the culture and family you live in. Getting help from trained professionals is important for healing
                                            from both. Click on "Schedule Consultation" below or on "Book Now" to take the first step right
                                            now.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    {/* Testimonial Section */}
                    <section className="mt-16 bg-purple bg-opacity-10 rounded-xl p-8">
                        <h2 className="text-3xl font-gentium-book-plus-regular text-charcol text-center mb-8">
                            Healing Stories
                        </h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {traumaTestimonials.map((testimonial, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-xl p-6 shadow-md relative"
                                >
                                    <img
                                        src={testimonial.avatar}
                                        alt="Client Testimonial"
                                        className="w-16 h-16 rounded-full absolute -top-8 left-1/2 transform -translate-x-1/2 border-4 border-white shadow-md"
                                    />
                                    <div className="text-center mt-8">
                                        <p className="font-lato-regular text-sage italic mb-4">
                                            "{testimonial.quote}"
                                        </p>
                                        <h3 className="font-raleway-regular text-charcol">
                                            Anonymous Client
                                        </h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    <WhatsAppCommunityBanner />

                    {/* Call to Action */}
                    <section className="mt-16 bg-golden-yellow bg-opacity-10 rounded-xl p-12 text-center">
                        <h2 className="text-3xl font-gentium-book-plus-regular text-charcol mb-6">
                            Begin Your Healing Journey
                        </h2>
                        <p className="text-xl font-lato-regular text-sage max-w-2xl mx-auto mb-8">
                            Every step towards healing is courageous. We're here to support you with
                            compassion, expertise, and understanding.
                        </p>
                        <div className="flex flex-col md:flex-row justify-center space-x-4 md:space-x-4 space-y-4 md:space-y-0 w-full max-w-md mx-auto">
                            <Link
                                className="bg-teal-700 text-white px-8 py-3 rounded-lg hover:bg-opacity-90 transition-colors font-raleway-regular"
                                to="/book-appointment"
                            >
                                Schedule Consultation
                            </Link>

                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default TraumaPage;